/**
 * SubscribeDriver — the derived "pay for your plan inside onboarding" step
 * (PE-1643, Phase 2b). A paid-intent org (onboardingSelectedPlan ∈ {BASE,PLUS})
 * pays via Stripe Embedded Checkout BEFORE reaching the rest of activation. The
 * step is DERIVED (no stored onboardingCurrentStep === 'subscribe'); whether it
 * shows is computed by shouldShowSubscribeStep from server state alone, so it
 * resumes on any device with no client flag (property 5).
 *
 * Completion = the WEBHOOK flipping billingDetails.plan to the intent — NOT the
 * embedded onComplete callback. On onComplete we enter a "confirming…" state and
 * poll GET /merchant until billingDetails.plan === intent; then shouldShowSubscribeStep
 * is false and this unmounts onto invite_team_pre_form. We NEVER force-downgrade
 * on a timeout (property 4) — an unconfirmed poll hands off to Phase 4
 * reconciliation (out of scope here).
 *
 * The whole step is gated behind isPricingBackendEnabled(): flag off ⇒ this
 * renders nothing and the existing funnel is byte-for-byte unchanged (the caller
 * also guards, this is belt-and-suspenders).
 *
 * Stripe libs: the dashboard's declared @stripe/react-stripe-js +
 * @stripe/stripe-js (same as EmbeddedCheckoutModal). The subscription is on the
 * PLATFORM account, so loadStripe takes only VITE_APP_STRIPE_PK (no stripeAccount).
 *
 * FE writes NO billingDetails — only the webhook does (§11a/§11b). The
 * decline-to-Starter exit clears INTENT via accounts-backend's PATCH
 * /onboarding/state (coordinated, not implemented here).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import posthog from 'posthog-js';
import { CATALOG } from '@charitystack/entitlements';
import { accountsClient, isAccountsRouteConfigured } from '../../../lib/trpc/accounts';

import { createEmbeddedCheckout, isPricingBackendEnabled } from '../../../api/pricing';
import { getAuthToken } from '../../../utils/auth';
import { safeCapture } from '../../../utils/analytics';
import usePersistOnboardingState from '../shared/usePersistOnboardingState';
import { getCurrentPlan } from '../shared/subscribeStepComplete';
import {
  EntitlementsProvider,
  useEntitlements,
} from '../../../contexts/EntitlementsContext';
import { useSmsFlagEnabled } from '../../../hooks/useMarketingTierEnabled';
import { PlanContextPanel } from '../../settings/account/billing/PlanContextPanel';
import { CardGlyph } from '../../settings/account/billing/EmbeddedCheckoutModal';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '../../../components/ui/dialog';
// The shared billing/checkout stylesheet — supplies the `.cs-billing` plan-card
// tokens and the `.cs-checkout-*` two-column layout this popup reuses, plus the
// `.cs-checkout-head-sub` rule (the trial sub-line) at the end of the file (PE-1637).
import '../../settings/account/billing/billing-plans-page.css';

// loadStripe must be called ONCE at module scope (re-creating it remounts the
// Stripe instance). The platform publishable key is a public VITE_* value; a
// missing key → null → we render an error rather than calling loadStripe(undefined).
const STRIPE_PK = import.meta.env.VITE_APP_STRIPE_PK;
const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : null;

// Poll cadence for the "confirming…" bridge: a bounded, backed-off refetch of
// GET /merchant waiting for the webhook to flip billingDetails.plan. We do NOT
// downgrade when it exhausts — Phase 4 reconciliation is the backstop. ~30s of
// polling (8 attempts, 1s→capped 5s) covers the common webhook latency.
const POLL_MAX_ATTEMPTS = 8;
const POLL_BASE_MS = 1000;
const POLL_MAX_MS = 5000;

// How long to let the dialog's close animation play before pushing the merchant
// update that unmounts this step (smooth handoff to the invite popup). Matches
// the Radix DialogContent `duration-200` close animation, +a small buffer.
const DIALOG_CLOSE_MS = 240;

const tierTrialDays = (plan) => CATALOG.trialDays?.[plan?.toLowerCase()] ?? 0;

/**
 * @param {object} props
 * @param {object} props.merchant
 * @param {function} props.setMerchant
 * @param {'BASE'|'PLUS'} props.selectedPlan - the resolved subscribe intent (uppercase)
 * @param {'monthly'|'yearly'} [props.billingPeriod]
 */
function SubscribeDriverInner({
  merchant,
  setMerchant,
  selectedPlan,
  billingPeriod = 'monthly',
}) {
  const persistStep = usePersistOnboardingState();
  // `can` feeds the context panel's grandfather-override hiding. A brand-new
  // onboarding org is implicit STARTER, so `can()` is false on every higher-tier
  // key → nothing is hidden and the full feature list shows (correct at signup).
  const { can } = useEntitlements();
  // The fee mode the context panel's fee line reflects. Same source as the
  // billing page (billingDetails.coveredCommitment), and the same correction:
  // an ABSENT value means Standard, not Commitment. Both entitlement engines
  // default absent to uncovered (resolver.ts:508 `?? false`; org_utils.py:689
  // `default=False`), so defaulting the panel to Commitment made it advertise
  // a fee line the charge path would not apply. accounts-backend now stamps
  // `false` at org creation, so a new org is explicitly Standard here.
  const feeMode =
    merchant?.billingDetails?.coveredCommitment === true ? 'commitment' : 'standard';
  // The context panel here is UPSELL copy (advertising the plan being purchased),
  // so it's gated on the SMS master flag ONLY — not the org's current plan — the
  // same way the billing page advertises SMS to not-yet-Plus orgs.
  const smsEnabled = useSmsFlagEnabled();
  // 'checkout' → embedded form shown; 'confirming' → paid, polling for the flip;
  // 'declining' → calling the decline-to-Starter exit.
  const [phase, setPhase] = useState('checkout');
  const [error, setError] = useState(null);
  // Drives the Radix Dialog's own `open`. We flip it to false on a confirmed plan
  // flip BEFORE pushing the merchant update that unmounts this step — so Radix
  // plays the dialog's close (fade/zoom-out) animation and the InviteTeamPopup
  // fades in over the SAME frosted overlay, instead of a hard snap (PE-1637).
  const [dialogOpen, setDialogOpen] = useState(true);
  const viewedRef = useRef(false);
  const pollCancelledRef = useRef(false);

  const orgId =
    merchant?.organizationId || merchant?.merchantID || merchant?.id || undefined;
  const trialDays = tierTrialDays(selectedPlan);
  const isTrial = trialDays > 0;

  // onboarding_subscribe_viewed — fire ONCE when the step first renders (i.e.
  // showSubscribe became true). Reaching the subscription screen.
  useEffect(() => {
    if (viewedRef.current) return;
    viewedRef.current = true;
    safeCapture(posthog, 'onboarding_subscribe_viewed', {
      plan: selectedPlan,
      is_trial: isTrial,
      trial_days: trialDays,
      billing_period: billingPeriod,
      org_id: orgId,
    });
  }, [selectedPlan, isTrial, trialDays, billingPeriod, orgId]);

  useEffect(() => {
    return () => {
      pollCancelledRef.current = true;
    };
  }, []);

  // Refetch GET /merchant and return the parsed record (or null on failure).
  const refetchMerchant = useCallback(async () => {
    if (!isAccountsRouteConfigured('GET /merchant')) return null;
    const result = await accountsClient.requestJson(
      'GET /merchant',
      { authorization: await getAuthToken(), input: {} },
      { context: '[onboarding] subscribe confirm poll', retries: 1, retryDelay: 1000 },
    );
    return result.ok ? result.data : null;
  }, []);

  // The "confirming…" bridge: poll until billingDetails.plan === selectedPlan,
  // pushing each fresh merchant into App state so shouldShowSubscribeStep
  // re-evaluates. NEVER downgrades — if the poll exhausts, we leave the user on
  // a "still confirming" message and Phase 4 reconciliation takes over.
  const beginConfirmPoll = useCallback(async () => {
    pollCancelledRef.current = false;
    for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt += 1) {
      if (pollCancelledRef.current) return;
      const fresh = await refetchMerchant();
      if (pollCancelledRef.current) return;
      if (fresh) {
        if (getCurrentPlan(fresh) === selectedPlan) {
          // Plan flipped. For a SMOOTH handoff to the invite step: play the
          // dialog's close animation FIRST (flip Radix `open` → false), let it
          // run, THEN push the fresh merchant — which is what flips
          // shouldShowSubscribeStep false and unmounts this step / mounts the
          // InviteTeamPopup. Pushing the merchant immediately (as before) yanked
          // the dialog out mid-frame with no exit animation (the snap). The
          // frosted overlay is shared by both dialogs, so it stays continuous
          // across the swap; only the inner cards cross-fade.
          setDialogOpen(false);
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => setTimeout(r, DIALOG_CLOSE_MS));
          if (pollCancelledRef.current) return;
          await persistStep({ onboardingCurrentStep: 'checklist', onboardingSelectedPlan: null });
          if (pollCancelledRef.current) return;
          setMerchant?.(fresh);
          return;
        }
        // Not flipped yet — keep App state fresh so other surfaces update while
        // we wait (this is the pre-flip path; the dialog stays open).
        setMerchant?.(fresh);
      }
      const delay = Math.min(POLL_BASE_MS * 2 ** attempt, POLL_MAX_MS);
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, delay));
    }
    // Poll exhausted without a confirmed flip. Do NOT downgrade. Surface a
    // gentle "still confirming" message; Phase 4 reconciliation is the backstop.
    if (!pollCancelledRef.current) {
      setError(
        "We're still confirming your subscription. This can take a moment — " +
          'you can keep waiting or refresh; your payment is safe.',
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetchMerchant, selectedPlan, setMerchant, persistStep]);

  // fetchClientSecret — the provider calls this on mount. Returns the Session's
  // client_secret. If the backend's first-completed-wins guard reports the org
  // is ALREADY subscribed (no clientSecret), skip straight to confirming — the
  // poll will see the plan flip (or it's already flipped) and unmount the step.
  const fetchClientSecret = useCallback(async () => {
    safeCapture(posthog, 'onboarding_subscribe_checkout_started', {
      plan: selectedPlan,
      billing_period: billingPeriod,
      org_id: orgId,
    });
    const result = await createEmbeddedCheckout({ plan: selectedPlan, billingPeriod });
    if (result.alreadySubscribed || !result.clientSecret) {
      // The org already has a live subscription — do not mount checkout. Enter
      // the confirming poll so the step resolves on the plan-match.
      setPhase('confirming');
      void beginConfirmPoll();
      // The SDK requires a string; returning a never-resolving promise keeps the
      // provider from erroring while we transition out of the checkout phase.
      return new Promise(() => {});
    }
    return result.clientSecret;
  }, [selectedPlan, billingPeriod, orgId, beginConfirmPoll]);

  // Stripe reports the embedded payment complete. Do NOT treat as subscribed:
  // enter the confirming poll and wait for the WEBHOOK to flip the plan.
  const handleComplete = useCallback(() => {
    safeCapture(posthog, 'onboarding_subscribe_confirming', {
      plan: selectedPlan,
      billing_period: billingPeriod,
      org_id: orgId,
    });
    setPhase('confirming');
    void beginConfirmPoll();
  }, [selectedPlan, billingPeriod, orgId, beginConfirmPoll]);

  // onboarding_subscribe_left:completed — best-effort on a confirmed unmount.
  // The RELIABLE funnel-close is the webhook flip (see PR notes); a server-side
  // capture is the optional Phase-4 follow-up.
  useEffect(() => {
    return () => {
      if (getCurrentPlan(merchant) === selectedPlan) {
        safeCapture(posthog, 'onboarding_subscribe_left', {
          plan: selectedPlan,
          outcome: 'completed',
          org_id: orgId,
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Belt-and-suspenders: never render if the pricing backend is off (the caller
  // also gates this). A flagged-off env is a no-op to the existing funnel.
  if (!isPricingBackendEnabled()) return <div className="m-6 rounded-md border border-edge bg-paper-light p-6" role="alert">Subscription checkout is temporarily unavailable. Please try again later to finish setting up your selected plan.</div>;

  const trialCopy = isTrial
    ? `Free for ${trialDays} days — card required, cancel anytime.`
    : `Start your ${titleCase(selectedPlan)} plan — billed today.`;

  const planName = titleCase(selectedPlan);

  // Rendered as a Radix Dialog POPUP over a frosted-glass overlay (the same
  // DialogContent + drawer-frosted-overlay InviteTeamPopup and the billing-page
  // EmbeddedCheckoutModal use) — the dashboard stays visible/blurred behind it.
  // The inner card is the modal's `.cs-checkout-*` two-column layout verbatim:
  // a sage-medallion header band over the shared <PlanContextPanel> (left) beside
  // the Stripe iframe (right). Matching the billing modal's width (820px) keeps
  // the two checkout surfaces visually identical.
  //
  // NON-DISMISSABLE (a required money step): backdrop click / Esc are no-ops
  // (onOpenChange does nothing) and the X is hidden (showX={false}). The only
  // exit is the decline-to-Starter link, which clears the paid intent server-side
  // so the step is resume-safe. `open` is always true while mounted — the parent
  // chrome unmounts the whole step (shouldShowSubscribeStep → false) when the
  // webhook flips the plan, at which point Radix plays the close animation and
  // the InviteTeamPopup fades in over the SAME overlay (the smooth crossfade).
  return (
    // `open` is driven by dialogOpen: true while the step is live; flipped false
    // on a confirmed plan flip so Radix plays the close animation before the
    // chrome unmounts this step (smooth handoff to the invite popup). NOT user-
    // dismissable — onOpenChange is a no-op; only our own flip closes it.
    <Dialog open={dialogOpen}>
      <DialogContent
        // `cs-billing` brings the plan-card tokens + @container into the portal;
        // `cs-checkout-modal` re-neutralizes the few page-root props that bleed.
        // `cs-onboarding-checkout` is a scoping hook for the onboarding-only
        // overrides so they never touch the billing modal. Height/scroll/width all
        // live in that scoped CSS — INTENTIONALLY not Tailwind utilities here:
        //   - max-height:90vh + overflow:hidden clamp the dialog box, and
        //   - the BODY (.cs-checkout-cols) is the one scroll owner.
        // A Tailwind `overflow-y-auto`/`max-h-[90vh]` on this element would tie the
        // CSS `overflow:hidden` on equal specificity and, if it won, scroll the
        // WHOLE dialog (pinned header included) — so it's deliberately omitted.
        // Responsive max-width also lives in the CSS. showX hidden — decline is the
        // only exit.
        className="cs-billing cs-checkout-modal cs-onboarding-checkout w-full p-0 gap-0"
        showX={false}
        aria-describedby="onboarding-subscribe-desc"
        // Belt-and-suspenders: even if a future Radix interaction tries to close
        // (Esc / outside-press), swallow it so the step can't be dismissed into a
        // half-state where intent is still set. Exit is the decline link only.
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        {/* HEADER BAND — sage medallion + title + trial reassurance (spans both
            columns), same treatment as the billing modal's `.cs-checkout-head`.
            The decline-to-Starter link sits TOP-RIGHT here (de-emphasized, the
            only sanctioned exit — there is no X). */}
        <div className="cs-checkout-head">
          <div className="cs-checkout-medallion">
            <CardGlyph />
          </div>
          <div className="cs-checkout-head-text">
            <DialogTitle className="cs-checkout-title">
              {phase === 'confirming'
                ? 'Confirming your subscription…'
                : `Set up your ${planName} plan`}
            </DialogTitle>
            {phase === 'checkout' ? (
              <DialogDescription
                id="onboarding-subscribe-desc"
                className="cs-checkout-head-sub"
              >
                {trialCopy}
              </DialogDescription>
            ) : (
              // Keep an aria-describedby target present in non-checkout phases so
              // the explicit aria-describedby above never dangles.
              <DialogDescription id="onboarding-subscribe-desc" className="sr-only">
                Set up your {planName} plan with Stripe.
              </DialogDescription>
            )}
          </div>


        </div>

        {error && (
          <div className="cs-onboarding-checkout-alert" role="alert">
            {error}
          </div>
        )}

        {/* TWO-COLUMN BODY — plan context (left) + Stripe iframe / state (right).
            Collapses to a single stacked column on mobile (CSS). */}
        <div className="cs-checkout-cols">
          {/* LEFT — the SHARED plan-context panel (same component the billing
              modal renders), so price/fee/feature copy can never drift. */}
          <div className="cs-checkout-context">
            <PlanContextPanel
              plan={selectedPlan}
              feeMode={feeMode}
              billingPeriod={billingPeriod}
              alreadyHasFeature={can}
              smsEnabled={smsEnabled}
            />
          </div>

          {/* RIGHT — Stripe iframe (checkout) or the confirming / error /
              no-key state, each in the same reserved-height frame so the popup
              stays a consistent size across phases. The iframe stays mounted in
              both 'checkout' and 'declining' (gated only by !confirming): the
              decline call is a quick async that either unmounts the whole step
              (success) or returns to 'checkout' (failure), so tearing the iframe
              down mid-decline would needlessly re-fetch a Stripe session. */}
          <div className="cs-checkout-body">
            {phase === 'confirming' ? (
              <div className="cs-checkout-frame cs-checkout-frame--centered">
                <span className="cs-checkout-spinner" aria-hidden />
                <p className="cs-checkout-state-text">
                  Payment received. We’re activating your plan — this usually
                  takes a few seconds.
                </p>
              </div>
            ) : !stripePromise ? (
              <div className="cs-checkout-frame cs-checkout-frame--centered">
                <p className="cs-checkout-state-text">
                  Checkout isn’t available right now. Please refresh and try
                  again, or contact support.
                </p>
              </div>
            ) : (
              <div
                key={`${selectedPlan}-${billingPeriod}`}
                className="cs-checkout-frame"
              >
                <EmbeddedCheckoutProvider
                  stripe={stripePromise}
                  options={{ fetchClientSecret, onComplete: handleComplete }}
                >
                  <EmbeddedCheckout />
                </EmbeddedCheckoutProvider>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Public entry: wrap the step in the EntitlementsProvider so the shared
// PlanContextPanel's compact PlanCard can resolve `can()` (a new onboarding org
// is implicit STARTER → nothing hidden, full feature list shows). The provider
// is a pure in-process resolve over merchant.billingDetails — no network call.
export default function SubscribeDriver(props) {
  // Dev State Lab changes displayed entitlements, not the token's real org.
  // Never open or complete a real subscription session for a simulated paid intent.
  if (props.merchant?.__devStateLabOverrides === true) {
    return <div className="m-6 rounded-lg border border-edge bg-paper-light p-6" role="status">
      <h2 className="text-lg font-medium text-ink">Complete your {titleCase(props.selectedPlan)} subscription</h2>
      <p className="mt-2 text-sm text-ink-secondary">Subscription checkout is required before entering Home.</p>
      <p className="mt-2 text-xs text-ink-muted">Dev State Lab preview. Checkout is disabled for simulated organizations.</p>
    </div>;
  }
  return (
    <EntitlementsProvider merchant={props.merchant}>
      <SubscribeDriverInner {...props} />
    </EntitlementsProvider>
  );
}

function titleCase(plan) {
  if (typeof plan !== 'string' || plan.length === 0) return 'paid';
  return plan.charAt(0) + plan.slice(1).toLowerCase();
}
